import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDocument, useCollection, useFirestoreMutations } from '../../hooks/useFirestore'
import { syncReciprocals } from '../../lib/relationshipSync'
import ImageUploader from './ImageUploader'
import ImageCropModal from './ImageCropModal'
import RichTextEditor from './RichTextEditor'
import RelationshipEditor from './RelationshipEditor'
import RelationshipImport from './RelationshipImport'
import './AdminEditor.css'

const EMPTY_CHARACTER = {
  name: '',
  type: 'pc',
  active: true,
  hidden: false,
  deceased: false,
  linkedUserId: '',
  knownCipherIds: [],
  class: '',
  race: '',
  gender: '',
  age: '',
  alignment: '',
  height: '',
  weight: '',
  hair: '',
  eyes: '',
  skin: '',
  faith: '',
  faction: '',
  profileImageUrl: '',
  profileImageCrop: null,
  trueFormImageUrl: '',
  trueFormImageCrop: null,
  trueFormRevealed: false,
  dndBeyondUrl: '',
  lore: '',
  eventIds: [],
  relationships: [],
  sortOrder: 0
}

const GENDER_OPTIONS = [
  { id: '', label: 'Not specified' },
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
  { id: 'nonbinary', label: 'Nonbinary' },
  { id: 'other', label: 'Other' },
]

export default function CharacterEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: existingCharacter, loading } = useDocument('characters', isNew ? null : id)
  const { data: allCharacters } = useCollection('characters', { orderByField: 'name' })
  const { data: allUsers } = useCollection('users', { orderByField: 'email' })
  const { data: allCiphers } = useCollection('ciphers', { orderByField: 'sortOrder' })
  const { add, update, remove } = useFirestoreMutations('characters')

  const [character, setCharacter] = useState(EMPTY_CHARACTER)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [showImport, setShowImport] = useState(false)
  const [showCropModal, setShowCropModal] = useState(false)
  const [showTrueFormCropModal, setShowTrueFormCropModal] = useState(false)

  // Track original relationships for bidirectional sync
  const originalRelationshipsRef = useRef([])

  useEffect(() => {
    if (existingCharacter) {
      setCharacter({ ...EMPTY_CHARACTER, ...existingCharacter })
      // Store original relationships for diff on save
      originalRelationshipsRef.current = existingCharacter.relationships || []
    }
  }, [existingCharacter])

  const handleChange = (field, value) => {
    setCharacter(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      let characterId = id

      if (isNew) {
        characterId = await add(character)
      } else {
        await update(id, character)
      }

      // Sync reciprocal relationships to target characters
      await syncReciprocals(
        characterId,
        originalRelationshipsRef.current,
        character.relationships || []
      )

      navigate('/admin/characters')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${character.name}"? This cannot be undone.`)) {
      return
    }

    setSaving(true)
    try {
      await remove(id)
      navigate('/admin/characters')
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>{isNew ? 'New Character' : 'Edit Character'}</h1>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => navigate('/admin/characters')}
        >
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="editor-form">
        {error && <div className="error-banner">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              value={character.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              placeholder="Character name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              value={character.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              <option value="pc">Player Character (PC)</option>
              <option value="npc">Non-Player Character (NPC)</option>
            </select>
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={character.active !== false}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
              <span>Active in Campaign</span>
            </label>
            <span className="help-text">Inactive characters appear faded</span>
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={character.hidden === true}
                onChange={(e) => handleChange('hidden', e.target.checked)}
              />
              <span>Hidden (Spoiler)</span>
            </label>
            <span className="help-text">Only visible in admin</span>
          </div>

          <div className="form-group form-group-inline">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={character.deceased === true}
                onChange={(e) => handleChange('deceased', e.target.checked)}
              />
              <span>Deceased</span>
            </label>
            <span className="help-text">Mark as dead</span>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="linkedUserId">Linked User (Player)</label>
            <select
              id="linkedUserId"
              value={character.linkedUserId || ''}
              onChange={(e) => handleChange('linkedUserId', e.target.value || null)}
            >
              <option value="">No linked user</option>
              {allUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.displayName || user.email}
                </option>
              ))}
            </select>
            <span className="help-text">Player can edit this character and see hidden notes</span>
          </div>

          <div className="form-group">
            <label htmlFor="knownCipherIds">Known Ciphers</label>
            <select
              id="knownCipherIds"
              multiple
              value={character.knownCipherIds || []}
              onChange={(e) => handleChange('knownCipherIds',
                Array.from(e.target.selectedOptions, opt => opt.value)
              )}
              style={{ minHeight: '80px' }}
            >
              {allCiphers.map(cipher => (
                <option key={cipher.id} value={cipher.id}>
                  {cipher.name}
                </option>
              ))}
            </select>
            <span className="help-text">Ctrl/Cmd+click to select multiple. Player can use these ciphers.</span>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="class">Class</label>
            <input
              id="class"
              type="text"
              value={character.class}
              onChange={(e) => handleChange('class', e.target.value)}
              placeholder="e.g., Fighter, Wizard, Rogue"
            />
          </div>

          <div className="form-group">
            <label htmlFor="race">Race</label>
            <input
              id="race"
              type="text"
              value={character.race}
              onChange={(e) => handleChange('race', e.target.value)}
              placeholder="e.g., Human, Elf, Dwarf"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              value={character.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
            >
              {GENDER_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="age">Age</label>
            <input
              id="age"
              type="text"
              value={character.age}
              onChange={(e) => handleChange('age', e.target.value)}
              placeholder="e.g., 32 or 'Young adult'"
            />
          </div>

          <div className="form-group">
            <label htmlFor="alignment">Alignment</label>
            <input
              id="alignment"
              type="text"
              value={character.alignment}
              onChange={(e) => handleChange('alignment', e.target.value)}
              placeholder="e.g., Chaotic Good"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="height">Height</label>
            <input
              id="height"
              type="text"
              value={character.height}
              onChange={(e) => handleChange('height', e.target.value)}
              placeholder={'e.g., 5\'10"'}
            />
          </div>

          <div className="form-group">
            <label htmlFor="weight">Weight</label>
            <input
              id="weight"
              type="text"
              value={character.weight}
              onChange={(e) => handleChange('weight', e.target.value)}
              placeholder="e.g., 165 lbs"
            />
          </div>

          <div className="form-group">
            <label htmlFor="faith">Faith</label>
            <input
              id="faith"
              type="text"
              value={character.faith}
              onChange={(e) => handleChange('faith', e.target.value)}
              placeholder="e.g., Lolth, Torm"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="hair">Hair</label>
            <input
              id="hair"
              type="text"
              value={character.hair}
              onChange={(e) => handleChange('hair', e.target.value)}
              placeholder="e.g., Black, long"
            />
          </div>

          <div className="form-group">
            <label htmlFor="eyes">Eyes</label>
            <input
              id="eyes"
              type="text"
              value={character.eyes}
              onChange={(e) => handleChange('eyes', e.target.value)}
              placeholder="e.g., Amber"
            />
          </div>

          <div className="form-group">
            <label htmlFor="skin">Skin</label>
            <input
              id="skin"
              type="text"
              value={character.skin}
              onChange={(e) => handleChange('skin', e.target.value)}
              placeholder="e.g., Obsidian"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="faction">Faction / Organization</label>
            <input
              id="faction"
              type="text"
              value={character.faction}
              onChange={(e) => handleChange('faction', e.target.value)}
              placeholder="e.g., House Baenre, Zhentarim, The Harpers"
            />
          </div>

          <div className="form-group">
            <label htmlFor="sortOrder">Sort Order</label>
            <input
              id="sortOrder"
              type="number"
              value={character.sortOrder}
              onChange={(e) => handleChange('sortOrder', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="dndBeyondUrl">D&D Beyond URL</label>
          <input
            id="dndBeyondUrl"
            type="url"
            value={character.dndBeyondUrl}
            onChange={(e) => handleChange('dndBeyondUrl', e.target.value)}
            placeholder="https://www.dndbeyond.com/characters/..."
          />
        </div>

        <div className="form-group">
          <label>Profile Image</label>
          <ImageUploader
            value={character.profileImageUrl}
            onChange={(url) => handleChange('profileImageUrl', url)}
            folder="characters"
          />
          {character.profileImageUrl && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowCropModal(true)}
              style={{ marginTop: 'var(--space-sm)' }}
            >
              Adjust Crop / Position
            </button>
          )}
        </div>

        <fieldset className="form-section">
          <legend>True Form (Optional)</legend>
          <p className="help-text" style={{ marginTop: 0, marginBottom: 'var(--space-md)' }}>
            For characters using illusion magic to disguise their true appearance.
            When revealed, this image replaces the profile image across the site.
          </p>

          <div className="form-group">
            <label>True Form Image</label>
            <ImageUploader
              value={character.trueFormImageUrl}
              onChange={(url) => handleChange('trueFormImageUrl', url)}
              folder="characters"
            />
            {character.trueFormImageUrl && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowTrueFormCropModal(true)}
                style={{ marginTop: 'var(--space-sm)' }}
              >
                Adjust Crop / Position
              </button>
            )}
          </div>

          {character.trueFormImageUrl && (
            <div className="form-group form-group-inline">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={character.trueFormRevealed === true}
                  onChange={(e) => handleChange('trueFormRevealed', e.target.checked)}
                />
                <span>Reveal True Form</span>
              </label>
              <span className="help-text">
                When checked, true form replaces profile image everywhere
              </span>
            </div>
          )}
        </fieldset>

        <div className="form-group">
          <label>Lore / Background</label>
          <RichTextEditor
            value={character.lore}
            onChange={(html) => handleChange('lore', html)}
            placeholder="Write the character's backstory and lore..."
          />
        </div>

        <RelationshipEditor
          relationships={character.relationships || []}
          characters={allCharacters}
          currentCharacterId={id}
          onChange={(relationships) => handleChange('relationships', relationships)}
          onImportClick={() => setShowImport(true)}
        />

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Character' : 'Save Changes')}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Character
            </button>
          )}
        </div>
      </form>

      <RelationshipImport
        isOpen={showImport}
        onClose={() => setShowImport(false)}
      />

      {showCropModal && character.profileImageUrl && (
        <ImageCropModal
          imageUrl={character.profileImageUrl}
          initialCrop={character.profileImageCrop}
          onSave={(cropData) => {
            handleChange('profileImageCrop', cropData)
            setShowCropModal(false)
          }}
          onClose={() => setShowCropModal(false)}
        />
      )}

      {showTrueFormCropModal && character.trueFormImageUrl && (
        <ImageCropModal
          imageUrl={character.trueFormImageUrl}
          initialCrop={character.trueFormImageCrop}
          onSave={(cropData) => {
            handleChange('trueFormImageCrop', cropData)
            setShowTrueFormCropModal(false)
          }}
          onClose={() => setShowTrueFormCropModal(false)}
        />
      )}
    </div>
  )
}
